"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SalesOrderRefund = void 0;
const class_validator_1 = require("class-validator");
const cloneModelObject_1 = require("../../../../apis/spg/util/cloneModelObject");
const errorTypes_1 = require("../../../../apis/spg/helpers/errorTypes");
const User_1 = require("../../../../apis/spg/models/User");
const SalesOrderRefundProduct_1 = require("./SalesOrderRefundProduct");
const SalesOrderRefundPromotion_1 = require("./SalesOrderRefundPromotion");
/**
 * Refund information on an order a customer made. The refund details include the manager who acted it, and reason they provided
 *
 * It also includes the items, quantity and price that was refunded. As well as any promotions to be remove that were on the original
 * sales order
 *
 * A refund can be done partially or in full
 */
class SalesOrderRefund {
    /**
     * Build a new refund model
     *
     * If a sales order is given as the argument then that refund will auto populate with its details
     */
    constructor(salesOrder, full) {
        // build data from given original sales order
        if (salesOrder) {
            // products and promotions that existed
            const { salesOrderItems, promotions } = salesOrder;
            // rebuild all products that were ordered so that can be refunded
            this.salesOrderRefundProducts = salesOrderItems.map((salesOrderItem) => {
                const salesOrderRefundProduct = new SalesOrderRefundProduct_1.SalesOrderRefundProduct();
                salesOrderRefundProduct.product = salesOrderItem;
                salesOrderRefundProduct.quantity = full ? salesOrderItem.quantity : 0;
                return salesOrderRefundProduct;
            });
            // promotions existed?
            // rebuild all promotions that were on the original so that can be removed
            if (Array.isArray(promotions) && promotions.length > 0) {
                this.salesOrderRefundPromotions = promotions.map((promotion) => {
                    const salesOrderRefundPromotion = new SalesOrderRefundPromotion_1.SalesOrderRefundPromotion();
                    salesOrderRefundPromotion.promotion = promotion;
                    salesOrderRefundPromotion.quantity = full ? 1 : 0; // initially set to not selected to force the user to make a decision
                    return salesOrderRefundPromotion;
                });
            }
            else {
                // no promotions
                this.salesOrderRefundPromotions = [];
            }
        }
    }
    /**
     * Total price refunded to the customer.
     * This is either:
     *
     * - The sum of all the refunded products and promotions removed.
     * - A fixed price given by the user that is not negative and no more than the original sales order amount
     */
    get priceRefunded() {
        const { overridePrice, productsRefundTotal, promotionsRemovedTotal } = this;
        return parseFloat((overridePrice || productsRefundTotal - promotionsRemovedTotal).toFixed(2));
    }
    /**
     * Total amount to refund for just products
     */
    get productsRefundTotal() {
        return parseFloat(this.salesOrderRefundProducts
            .reduce((total, current) => total + current.priceRefunded, 0)
            .toFixed(2));
    }
    /**
     * Total amnount of promotions removed in refund
     */
    get promotionsRemovedTotal() {
        return parseFloat(this.salesOrderRefundPromotions
            .reduce((total, current) => total - current.priceRefunded, 0)
            .toFixed(2));
    }
    get totalRefundedItems() {
        return (this.salesOrderRefundProducts.length +
            this.salesOrderRefundPromotions.length);
    }
    /**
     * Whether everything in the original order is being refunded
     */
    get isFullRefund() {
        // all products being refunded must be that same quantity as what was originally ordered
        const allProductsRefund = this.salesOrderRefundProducts.every((salesOrderRefundProduct) => salesOrderRefundProduct.quantity ===
            salesOrderRefundProduct.product.quantity);
        // all promotions removed must be selected
        const allPromotionsRemoved = this.salesOrderRefundPromotions.every((salesOrderRefundPromotion) => salesOrderRefundPromotion.quantity > 0);
        // all products refunded and promotions removed
        return allProductsRefund && allPromotionsRemoved;
    }
    /**
     * Whether products have been refunded
     */
    get hasRefundProducts() {
        const { overridePrice, salesOrderRefundProducts } = this;
        return (!overridePrice &&
            Array.isArray(salesOrderRefundProducts) &&
            salesOrderRefundProducts.length > 0);
    }
    /**
     * Whether products have been removed as part of the refund
     */
    get hasRefundPromotions() {
        const { salesOrderRefundPromotions } = this;
        return (Array.isArray(salesOrderRefundPromotions) &&
            salesOrderRefundPromotions.length > 0);
    }
    /**
     * Whether either products have been refunded or promotions removed
     */
    get hasRefundItems() {
        const { hasRefundProducts, hasRefundPromotions } = this;
        return hasRefundProducts || hasRefundPromotions;
    }
    /**
     * Transforms sales order refund data such that it can be consumed by a comms
     * template
     */
    get commsData() {
        const { priceRefunded, hasRefundProducts, hasRefundPromotions, hasRefundItems, salesOrderRefundProducts, salesOrderRefundPromotions } = this;
        return Object.assign(Object.assign({}, this.serialize()), { priceRefunded: priceRefunded.toFixed(2), hasRefundProducts,
            hasRefundPromotions,
            hasRefundItems, salesOrderRefundProducts: hasRefundProducts
                ? salesOrderRefundProducts.map((salesOrderRefundProduct) => salesOrderRefundProduct.commsData)
                : undefined, salesOrderRefundPromotions: hasRefundPromotions
                ? salesOrderRefundPromotions.map((salesOrderRefundPromotion) => salesOrderRefundPromotion.commsData)
                : undefined });
    }
    filterSensitiveData() {
        const clone = (0, cloneModelObject_1.cloneModelObject)(this);
        return clone;
    }
    serialize() {
        return {
            salesOrderRefundId: this.salesOrderRefundId,
            overridePrice: this.overridePrice,
            comment: this.comment,
            createdAt: this.updatedAt,
            updatedAt: this.createdAt,
            user: this.user ? this.user.serialize() : null,
            salesOrderRefundProducts: this.salesOrderRefundProducts.map((salesOrderRefundProduct) => salesOrderRefundProduct.serialize()),
            salesOrderRefundPromotions: this.salesOrderRefundPromotions.map((salesOrderRefundPromotion) => salesOrderRefundPromotion.serialize())
        };
    }
    static unserialize(data) {
        const newSalesOrderRefund = new this();
        newSalesOrderRefund.salesOrderRefundId = data.salesOrderRefundId;
        newSalesOrderRefund.overridePrice = data.overridePrice;
        newSalesOrderRefund.comment = data.comment;
        newSalesOrderRefund.createdAt = data.createdAt;
        newSalesOrderRefund.updatedAt = data.updatedAt;
        newSalesOrderRefund.user = data.user ? User_1.User.unserialize(data.user) : null;
        newSalesOrderRefund.salesOrderRefundProducts = data.salesOrderRefundProducts.map((salesOrderRefundProduct) => SalesOrderRefundProduct_1.SalesOrderRefundProduct.unserialize(salesOrderRefundProduct));
        newSalesOrderRefund.salesOrderRefundPromotions = data.salesOrderRefundPromotions.map((salesOrderRefundPromotion) => SalesOrderRefundPromotion_1.SalesOrderRefundPromotion.unserialize(salesOrderRefundPromotion));
        return newSalesOrderRefund;
    }
    async validate(salesOrder) {
        // check field validators
        const validated = await (0, class_validator_1.validate)(this);
        if (validated.length > 0) {
            throw new errorTypes_1.ValidationError(validated);
        }
        // refund data
        const { salesOrderRefundProducts, salesOrderRefundPromotions, priceRefunded, overridePrice } = this;
        // original sales order data
        const { salesOrderItems: originalSalesOrderItems, totalPrice, promotions: originalPromotions } = salesOrder;
        // refund price is the fixed price given by the user or the calculation of products refunded and promotions removed
        const totalRefund = overridePrice || priceRefunded;
        // refund amount cannot be negative
        if (totalRefund < 0) {
            throw new errorTypes_1.ValidationError("Cannot refund a negative amount");
        }
        // refund amount can't be more than the original order total
        if (totalRefund > totalPrice) {
            throw new errorTypes_1.ValidationError("Refund is more than the original order total price");
        }
        // all products must exist in the original order
        salesOrderRefundProducts.forEach((salesOrderRefundProduct) => {
            // refund product data
            const { product } = salesOrderRefundProduct;
            // check if product exists
            const productExists = originalSalesOrderItems.some((originalSalesOrderItem) => product.productId === originalSalesOrderItem.productId);
            if (!productExists) {
                throw new errorTypes_1.ValidationError(`The refund product (${product.title}) doesn't exist in original order`);
            }
            // we check valid quantities in the refund product model validator
        });
        // all promotions must exist in the original order
        salesOrderRefundPromotions.forEach((salesOrderRefundPromotion) => {
            const { promotion: { description } } = salesOrderRefundPromotion;
            const promotionExists = originalPromotions.some((originalPromotion) => description === originalPromotion.description);
            if (!promotionExists) {
                throw new errorTypes_1.ValidationError(`The refund promotion (${description}) doesn't exist in original order`);
            }
        });
        return true;
    }
}
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrderRefund.prototype, "salesOrderRefundId", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsNotEmpty)()
], SalesOrderRefund.prototype, "comment", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrderRefund.prototype, "overridePrice", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrderRefund.prototype, "createdAt", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrderRefund.prototype, "updatedAt", void 0);
__decorate([
    (0, class_validator_1.IsNotEmpty)(),
    (0, class_validator_1.ValidateNested)()
], SalesOrderRefund.prototype, "user", void 0);
__decorate([
    (0, class_validator_1.IsArray)(),
    (0, class_validator_1.ValidateNested)()
], SalesOrderRefund.prototype, "salesOrderRefundProducts", void 0);
__decorate([
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.IsArray)(),
    (0, class_validator_1.ValidateNested)()
], SalesOrderRefund.prototype, "salesOrderRefundPromotions", void 0);
exports.SalesOrderRefund = SalesOrderRefund;
//# sourceMappingURL=SalesOrderRefund.js.map